import type { ReactNode } from 'react';
import { UserName } from '../ui/UserName';
import type { AccountModelProvider } from '../../types/accountModels';
import type { ModelProvider, ModelProviderKeySource } from '../../types';
import { formatRelativeTime } from '../../utils/dateUtils';

/**
 * One Model Provider as a row with its key chip (task 1167) — extracted from
 * Settings › Account › Models (task 1147) so the platform Models page reads a
 * key the same way an account owner does.
 *
 * Report: `project/reports/innovate-app-model-catalog-unified-design.md`, "Showing the Key".
 *
 * `scope` picks whose key the row describes:
 *
 * - **`account`** — the shipped account row, unchanged: "Platform key" or
 *   "Your key · ····3fQ2", who added it, how many Models it reaches, and
 *   Use your own key / Replace / Verify / Remove.
 * - **`platform`** — the platform's own key, from the provider read's
 *   `keySource` (task 1166): Installed key, Stored key, Stored key over
 *   installed, No key; each with its last four and its Test connection state
 *   (Verified, Not tested, Failed), and the actions that state allows.
 *
 * Layout is the same in both: initial, name, chip, badges, fact lines, then
 * the actions on the right. The row is the inner block; the caller supplies
 * the list item (or card row) around it.
 */

const SMALL_BUTTON =
  'inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50';
export const ROW_GHOST_BUTTON = `${SMALL_BUTTON} border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`;
export const ROW_PRIMARY_BUTTON = `${SMALL_BUTTON} border-transparent bg-indigo-600 text-white hover:bg-indigo-700`;
export const ROW_DANGER_BUTTON = `${SMALL_BUTTON} border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30`;

const CHIP = 'rounded-full px-2 py-0.5 text-xs font-semibold';
const CHIP_NEUTRAL = `${CHIP} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`;
const CHIP_GOOD = `${CHIP} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`;
const CHIP_BAD = `${CHIP} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300`;
const CHIP_WARN = `${CHIP} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`;

/** True when the last failure is newer than the last success (or there is no success). */
export function failedSinceVerified(provider: Pick<AccountModelProvider, 'lastFailedAt' | 'verifiedAt'>): boolean {
  if (!provider.lastFailedAt) return false;
  if (!provider.verifiedAt) return true;
  return new Date(provider.lastFailedAt).getTime() >= new Date(provider.verifiedAt).getTime();
}

// ---------------------------------------------------------------------------
// Platform key state
// ---------------------------------------------------------------------------

export type PlatformKeyVerification = 'verified' | 'untested' | 'failed';

export interface PlatformKeyState {
  source: ModelProviderKeySource;
  /** The last four of the key calls actually use; null with no key. */
  lastFour: string | null;
  verification: PlatformKeyVerification;
}

/**
 * The platform key's state, from the provider read.
 *
 * A service that predates task 1166 sends no `keySource`; the state is then
 * inferred from the stored key alone (stored, or none), which is what the page
 * could say before.
 */
export function platformKeyState(provider: ModelProvider): PlatformKeyState {
  const source: ModelProviderKeySource = provider.keySource ?? (provider.apiKeyLastFour ? 'stored' : 'none');
  const lastFour =
    source === 'installed'
      ? provider.installedKeyLastFour ?? null
      : source === 'none'
        ? null
        : provider.apiKeyLastFour ?? null;
  const verification: PlatformKeyVerification = provider.lastVerifyError
    ? 'failed'
    : provider.verifiedAt
      ? 'verified'
      : 'untested';
  return { source, lastFour, verification };
}

const SOURCE_LABELS: Record<ModelProviderKeySource, string> = {
  installed: 'Installed key',
  stored: 'Stored key',
  'stored-over-installed': 'Stored key',
  none: 'No key',
};

/** One line for the rail: "Installed key · verified 2h ago". */
export function platformKeySummary(provider: ModelProvider): string {
  const state = platformKeyState(provider);
  if (state.source === 'none') return 'No key';
  const label = SOURCE_LABELS[state.source];
  if (state.verification === 'failed') return `${label} · failed`;
  if (state.verification === 'untested') return `${label} · not tested`;
  return `${label} · verified ${formatRelativeTime(provider.verifiedAt as string)}`;
}

// ---------------------------------------------------------------------------
// The chip
// ---------------------------------------------------------------------------

type ChipProps =
  | { scope: 'account'; provider: AccountModelProvider }
  | { scope: 'platform'; provider: ModelProvider };

/** The key chip: whose key, its last four, and (account) when it was verified. */
export function ProviderKeyChip(props: ChipProps) {
  if (props.scope === 'account') {
    const { provider } = props;
    if (!provider.keyLastFour) {
      return (
        <span
          data-testid={`provider-key-status-${provider.key}`}
          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-300"
        >
          Platform key
        </span>
      );
    }
    return (
      <span
        data-testid={`provider-key-status-${provider.key}`}
        className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300"
      >
        Your key · <span className="font-mono">····{provider.keyLastFour}</span>
        {provider.verifiedAt && <> · verified {formatRelativeTime(provider.verifiedAt)}</>}
      </span>
    );
  }

  const { provider } = props;
  const state = platformKeyState(provider);
  if (state.source === 'none') {
    return (
      <span data-testid={`provider-key-status-${provider.key}`} data-source="none" className={CHIP_WARN}>
        No key
      </span>
    );
  }
  return (
    <span
      data-testid={`provider-key-status-${provider.key}`}
      data-source={state.source}
      className={state.verification === 'failed' ? CHIP_BAD : CHIP_GOOD}
    >
      {SOURCE_LABELS[state.source]}
      {state.lastFour && (
        <>
          {' '}
          · <span className="font-mono">····{state.lastFour}</span>
        </>
      )}
    </span>
  );
}

const formatVerifiedAt = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

/** Verified / Not tested / Failed beside the platform chip, or a spinner while a test runs. */
function PlatformVerification({ provider, testing }: { provider: ModelProvider; testing: boolean }) {
  const state = platformKeyState(provider);
  if (state.source === 'none') return null;
  if (testing) {
    return (
      <span role="status" className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600 dark:border-gray-600 dark:border-t-indigo-400"
        />
        Testing…
      </span>
    );
  }
  if (state.verification === 'failed') {
    return (
      <span data-testid={`provider-verify-${provider.key}`} className={CHIP_BAD}>
        Failed
      </span>
    );
  }
  if (state.verification === 'verified') {
    return (
      <span data-testid={`provider-verify-${provider.key}`} className="inline-flex flex-wrap items-center gap-1.5">
        <span className={CHIP_GOOD}>Verified</span>
        <time dateTime={provider.verifiedAt as string} className="text-xs text-gray-500 dark:text-gray-400">
          {formatVerifiedAt(provider.verifiedAt as string)}
        </time>
      </span>
    );
  }
  return (
    <span data-testid={`provider-verify-${provider.key}`} className={CHIP_NEUTRAL}>
      Not tested
    </span>
  );
}

// ---------------------------------------------------------------------------
// The row
// ---------------------------------------------------------------------------

function Initial({ name }: { name: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

interface AccountRowProps {
  scope: 'account';
  provider: AccountModelProvider;
  canManage: boolean;
  /** The key was just saved: the row carries the what-changed note. */
  showSavedNote: boolean;
  verifying: boolean;
  onUseOwnKey: () => void;
  onReplace: () => void;
  onVerify: () => void;
  onRemove: () => void;
  /** Rendered after the key actions (the account page's on/off switch). */
  trailing?: ReactNode;
}

interface PlatformRowProps {
  scope: 'platform';
  provider: ModelProvider;
  canManage: boolean;
  testing: boolean;
  onTest: () => void;
  /** Opens the Edit provider dialog on its key field (Replace, Add key). */
  onEditKey: () => void;
  onRemoveStoredKey: () => void;
  trailing?: ReactNode;
}

export type ProviderRowProps = AccountRowProps | PlatformRowProps;

export function ProviderRow(props: ProviderRowProps) {
  return props.scope === 'account' ? <AccountProviderRow {...props} /> : <PlatformProviderRow {...props} />;
}

function AccountProviderRow({
  provider,
  canManage,
  showSavedNote,
  verifying,
  onUseOwnKey,
  onReplace,
  onVerify,
  onRemove,
  trailing,
}: AccountRowProps) {
  const failed = failedSinceVerified(provider);
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Initial name={provider.displayName} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{provider.displayName}</span>
          <ProviderKeyChip scope="account" provider={provider} />
          {provider.isAccountDefault && (
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
              Default provider
            </span>
          )}
        </div>

        <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          {!provider.keyLastFour && (
            <p>Calls use Innovate&apos;s key · usage is billed on your invoice at your plan&apos;s rate.</p>
          )}
          {provider.keyLastFour && failed && provider.lastFailedAt && (
            <p className="text-red-600 dark:text-red-400" data-testid={`provider-key-failure-${provider.key}`}>
              Verification failed {formatRelativeTime(provider.lastFailedAt)}
              {provider.lastFailureMsg ? ` · ${provider.lastFailureMsg}` : ''}
            </p>
          )}
          {provider.keyLastFour && provider.addedBy && (
            <p data-testid={`provider-key-added-by-${provider.key}`}>
              Added by <UserName userId={provider.addedBy} />
              {provider.addedAt && <> on {provider.addedAt.slice(0, 10)}</>}
            </p>
          )}
          {provider.keyLastFour && provider.reachableCount !== null && provider.totalCount !== null && (
            <p data-testid={`provider-reachable-${provider.key}`}>
              {provider.reachableCount} of {provider.totalCount} models reachable on your key
            </p>
          )}
          {!provider.enabled && (
            <p className="font-medium text-amber-700 dark:text-amber-300" data-testid={`provider-off-${provider.key}`}>
              Provider off — all its models are disallowed
            </p>
          )}
        </div>

        {showSavedNote && provider.keyLastFour && (
          <p
            data-testid={`provider-key-saved-note-${provider.key}`}
            className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 p-2 text-xs text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200"
          >
            All of this account&apos;s AI Services now use your key for {provider.displayName}.{' '}
            {provider.displayName} bills you for those tokens, not Innovate.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canManage && !provider.keyLastFour && (
          <button type="button" className={ROW_PRIMARY_BUTTON} onClick={onUseOwnKey}>
            Use your own key
          </button>
        )}
        {canManage && provider.keyLastFour && (
          <>
            <button type="button" className={ROW_GHOST_BUTTON} onClick={onReplace}>
              Replace
            </button>
            <button type="button" className={ROW_GHOST_BUTTON} disabled={verifying} onClick={onVerify}>
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" className={ROW_DANGER_BUTTON} onClick={onRemove}>
              Remove
            </button>
          </>
        )}
        {trailing}
      </div>
    </div>
  );
}

function PlatformProviderRow({
  provider,
  canManage,
  testing,
  onTest,
  onEditKey,
  onRemoveStoredKey,
  trailing,
}: PlatformRowProps) {
  const state = platformKeyState(provider);
  const stored = state.source === 'stored' || state.source === 'stored-over-installed';
  const hasKey = state.source !== 'none';
  // Right after Add provider or Replace the key has never been tested: the one
  // useful action is to test it.
  const untestedOnly = hasKey && state.verification === 'untested';

  return (
    <div className="flex flex-wrap items-start gap-3" data-testid={`platform-key-row-${provider.key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <ProviderKeyChip scope="platform" provider={provider} />
          <PlatformVerification provider={provider} testing={testing} />
        </div>

        <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          {state.source === 'none' && (
            <p data-testid={`provider-no-key-${provider.key}`}>
              No key · calls fail until one is stored.
            </p>
          )}
          {state.source === 'installed' && (
            <p>
              Read from the environment at startup. Every account without its own key calls{' '}
              {provider.displayName} on it. Storing a key here takes over from the installed one.
            </p>
          )}
          {state.source === 'stored' && <p>Encrypted in Innovate.</p>}
          {state.source === 'stored-over-installed' && (
            <p data-testid={`provider-installed-unused-${provider.key}`}>
              An installed key
              {provider.installedKeyLastFour ? (
                <>
                  {' '}
                  <span className="font-mono">····{provider.installedKeyLastFour}</span>
                </>
              ) : null}{' '}
              is also present and is not used. Removing the stored key falls back to it.
            </p>
          )}
          {state.verification === 'failed' && provider.lastVerifyError && (
            <p className="text-red-600 dark:text-red-400" data-testid={`provider-key-failure-${provider.key}`}>
              Test connection failed · {provider.lastVerifyError}
            </p>
          )}
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          {!hasKey && (
            <button type="button" className={ROW_PRIMARY_BUTTON} onClick={onEditKey}>
              Add key
            </button>
          )}
          {hasKey && (
            <button type="button" className={ROW_GHOST_BUTTON} disabled={testing} onClick={onTest}>
              {testing ? 'Testing…' : 'Test connection'}
            </button>
          )}
          {hasKey && !untestedOnly && state.source === 'installed' && (
            <button type="button" className={ROW_GHOST_BUTTON} onClick={onEditKey}>
              Replace with stored key
            </button>
          )}
          {hasKey && !untestedOnly && stored && (
            <button type="button" className={ROW_GHOST_BUTTON} onClick={onEditKey}>
              Replace
            </button>
          )}
          {stored && !untestedOnly && state.verification !== 'failed' && (
            <button type="button" className={ROW_DANGER_BUTTON} onClick={onRemoveStoredKey}>
              Remove stored key
            </button>
          )}
          {trailing}
        </div>
      )}
    </div>
  );
}
